import { Flex } from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { CmButton } from '@/components/uiParts';
import { EllipsesButton } from '@/components/uiParts/Button/EllipsesButton';
import { myObjectKeys } from '@/global/function';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import {
  validateBuilding,
  validateCity,
  validateEmail,
  validateLatitude,
  validateLongitude,
  validateName,
  validatePhoneNumber,
  validatePrefecture,
  validateStreetAddress,
  validateZipCode,
} from '@/pages/store-management/auth-stores/components/AuthStoreSidebarForm/validate';
import { isEqualGeoLocation } from '@/pages/store-management/auth-stores/service';
import { SidebarWrapper } from '@/pages/store-management/components/Layout/SidebarWrapper';
import { StoreManagementAuthStoreForm } from '@/pages/store-management/components/StoreManagementAuthStoreForm/StoreManagementAuthStoreForm';
import { useStoreManagementAuthStoreForm } from '@/pages/store-management/components/StoreManagementAuthStoreForm/useStoreManagementAuthStoreForm';
import {
  StoreManagementAuthStoreShowReqponse,
  useStoreManagementAuthStoreUpdate,
} from '@/services/StoreManagement/AuthStores/hooks';
import { useStoreManagementAuthStoreDelete } from '@/services/StoreManagement/AuthStores/hooks/delete/useStoreManagementAuthStoreDelete';
type Props = {
  sidebarAuthStoreId: number;
  isSidebarOpen: boolean;
  targetAuthStore?: StoreManagementAuthStoreShowReqponse;
  handleCloseSidebar: () => void;
  handleInitializeAuthStores: () => Promise<void>;
};

export const AuthStoreSidebarForm: React.FC<Props> = ({
  sidebarAuthStoreId,
  isSidebarOpen,
  targetAuthStore,
  handleCloseSidebar,
  handleInitializeAuthStores,
}) => {
  const {
    isOpenAddressList,
    name,
    email,
    addFeatureIds,
    phoneNumber,
    zipCode,
    prefecture,
    addresses,
    city,
    streetAddress,
    building,
    initialGeoLocation,
    geoLocation,
    isLoading,
    initialStringifyFormState,
    error,
    setError,
    setIsLoading,
    setInitialStringifyFormState,
    handleInputName,
    handleInputEmail,
    handleChangeAddFeatures,
    handleInputPhoneNumber,
    handleInputZipCode,
    onCloseAddressList,
    handleSearchAdressByPosCode,
    handleSetAddress,
    handleInputCity,
    handleChangePrefecture,
    handleInputStreetAddress,
    handleInputBuilding,
    handleInputLatitude,
    handleInputLongitude,
    initializeEditState,
    initializeForm,
  } = useStoreManagementAuthStoreForm();

  const handleUpdate = async () => {
    // 最終確認のバリデーション
    const newError = { ...error };
    myObjectKeys(newError).forEach((key) => {
      newError[key].isTouched = true;
    });
    const isError = [
      validateName(name, error).name.isError,
      validateEmail(email, error).email.isError,
      validateZipCode(zipCode, error).zipCode.isError,
      validatePhoneNumber(phoneNumber, error).phoneNumber.isError,
      validatePrefecture(prefecture, error).prefecture.isError,
      validateCity(city, error).city.isError,
      validateStreetAddress(streetAddress, error).streetAddress.isError,
      validateBuilding(building, error).building.isError,
      validateLatitude(geoLocation.latitude, error).latitude.isError,
      validateLongitude(geoLocation.longitude, error).longitude.isError,
    ].some((err) => err);
    setError(newError);
    if (isError) {
      return alert(
        '不正な入力内容が確認されました。入力内容をご確認ください。'
      );
    }
    // ここまでがバリデーション
    setIsLoading(true);
    try {
      await useStoreManagementAuthStoreUpdate(sidebarAuthStoreId, {
        name,
        email,
        phone_number: phoneNumber,
        prefecture,
        city,
        street_address: streetAddress,
        building: building !== '' ? building : null,
        zip_code: zipCode,
        add_feature_ids: addFeatureIds,
        // 緯度・経度は変更があった場合のみ値を渡す
        ...(isEqualGeoLocation(geoLocation, initialGeoLocation)
          ? {
              latitude: null,
              longitude: null,
            }
          : {
              latitude: geoLocation.latitude,
              longitude: geoLocation.longitude,
            }),
      });
      await handleInitializeAuthStores();
      setIsLoading(false);
      alert('拠点情報を更新しました。');
      // ここで更新をしないと同じ拠点情報を開いたときにバリデーションの比較がずれる。
      setInitialStringifyFormState(
        JSON.stringify([
          name,
          email,
          phoneNumber,
          prefecture,
          city,
          streetAddress,
          building,
          zipCode,
          addFeatureIds,
          geoLocation,
        ])
      );
      initializeForm();
      handleCloseSidebar();
    } catch (error) {
      if (error instanceof HttpError && error.status === 400) {
        setIsLoading(false);
        return alert(error.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  const handleDelete = async () => {
    const checkDelete = confirm('拠点を削除しますか？');
    if (!checkDelete) {
      return;
    }

    try {
      await useStoreManagementAuthStoreDelete(sidebarAuthStoreId);
      alert('拠点を削除しました。');
      await handleInitializeAuthStores();
      setIsLoading(false);
      handleCloseSidebar();
    } catch (error) {
      if (error instanceof HttpError && error.status === 400) {
        setIsLoading(false);
        return alert(error.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };

  const isEdit =
    initialStringifyFormState !==
    JSON.stringify([
      name,
      email,
      phoneNumber,
      prefecture,
      city,
      streetAddress,
      building,
      zipCode,
      addFeatureIds,
      geoLocation,
    ]);

  const isDisabledUpdateButton = [
    error.name.isError,
    error.email.isError,
    error.phoneNumber.isError,
    error.prefecture.isError,
    error.city.isError,
    error.streetAddress.isError,
    error.building.isError,
    error.zipCode.isError,
    error.latitude.isError,
    error.longitude.isError,
    !isEdit,
  ].some((isError) => isError);

  useEffect(() => {
    (async () => {
      if (targetAuthStore === undefined) return;
      await initializeEditState(targetAuthStore);
    })();
  }, [targetAuthStore]);

  return (
    <SidebarWrapper
      {...{
        isSidebarOpen,
        handleCloseSidebar,
        isDisabledUpdateButton,
        buttonComponent: (
          <Flex>
            <CmButton
              mr="10px"
              onClick={handleUpdate}
              isDisabled={isDisabledUpdateButton}
            >
              更新
            </CmButton>
            <EllipsesButton
              menus={[
                {
                  label: '削除',
                  menuItemProps: {
                    _hover: { bg: 'rgba(0, 0, 0, 0.06)' },
                    onClick: handleDelete,
                    fontWeight: 'bold',
                    color: 'red',
                  },
                },
              ]}
              menuButtonProps={{
                _hover: { bg: 'rgba(0, 0, 0, 0.06)' },
                padding: '5px 10px',
              }}
            />
          </Flex>
        ),
      }}
    >
      <StoreManagementAuthStoreForm
        {...{
          authStoreId: sidebarAuthStoreId,
          isOpenAddressList,
          name,
          email,
          addFeatureIds,
          phoneNumber,
          zipCode,
          prefecture,
          addresses,
          city,
          streetAddress,
          building,
          geoLocation,
          isLoading,
          error,
          handleInputName,
          handleInputEmail,
          handleChangeAddFeatures,
          handleInputPhoneNumber,
          handleInputZipCode,
          onCloseAddressList,
          handleSearchAdressByPosCode,
          handleSetAddress,
          handleInputCity,
          handleChangePrefecture,
          handleInputStreetAddress,
          handleInputBuilding,
          handleInputLatitude,
          handleInputLongitude,
        }}
      />
    </SidebarWrapper>
  );
};
